import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ForemanDetailedSummary,
  ForemanDetailedSummarySource,
} from '@ups/xplat/api/dto';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { formatDateAsIsoLocal } from '@ups/xplat/utils';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ForemanDetailedSummaryService {
  httpFactory = inject(MyHttpClientFactory);
  http: HttpClient;

  constructor() {
    const url = environment.urls.spartaAPI;
    this.http = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  getDetailedSummary(
    foremanJobID: string,
    dateFrom: Date,
    dateTo: Date,
    source?: ForemanDetailedSummarySource
  ): Observable<ForemanDetailedSummary> {
    const query = `dateFrom=${formatDateAsIsoLocal(
      dateFrom
    )}&dateTo=${formatDateAsIsoLocal(dateTo)}${
      source ? `&source=${encodeURIComponent(source)}` : ''
    }`;

    return this.http
      .get(`/api/foreman/detailed-summary/${foremanJobID}?${query}`)
      .pipe(
        map((response) =>
          this.normalizeDetailedSummary(response as ForemanDetailedSummary)
        )
      );
  }

  private normalizeDetailedSummary(
    response: ForemanDetailedSummary
  ): ForemanDetailedSummary {
    const responseRecord = response as unknown as Record<string, unknown>;
    const rawRecords = (response?.records ??
      responseRecord.Records ??
      []) as Array<Record<string, unknown>>;
    const records = rawRecords.map((record) => ({
      source: this.asSource(record.source ?? record.Source),
      recordType: this.asRecordType(record.recordType ?? record.RecordType),
      workDate: this.asString(record.workDate ?? record.WorkDate),
      displayName: this.asString(record.displayName ?? record.DisplayName),
      employeeName: this.asString(record.employeeName ?? record.EmployeeName),
      workOrder: this.asString(record.workOrder ?? record.WorkOrder),
      shiftLabel: this.asString(record.shiftLabel ?? record.ShiftLabel),
      foremanCrew: this.asString(record.foremanCrew ?? record.ForemanCrew),
      earnCode: this.asNullableString(record.earnCode ?? record.EarnCode),
      hours: this.asNullableNumber(record.hours ?? record.Hours),
      travelHours: this.asNullableNumber(
        record.travelHours ?? record.TravelHours
      ),
      equipmentHours: this.asNullableNumber(
        record.equipmentHours ?? record.EquipmentHours
      ),
      hasPerDiem: this.asBoolean(record.hasPerDiem ?? record.HasPerDiem),
      perDiemCount: this.asNumber(record.perDiemCount ?? record.PerDiemCount),
      exportStatus: this.asNullableString(
        record.exportStatus ?? record.ExportStatus
      ),
      exportMessage: this.asNullableString(
        record.exportMessage ?? record.ExportMessage
      ),
    }));

    return {
      dateFrom: this.asString(response?.dateFrom ?? responseRecord.DateFrom),
      dateTo: this.asString(response?.dateTo ?? responseRecord.DateTo),
      records,
    };
  }

  private asString(value: unknown): string {
    if (value == null) {
      return '';
    }

    return String(value);
  }

  private asNullableString(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }

    return String(value);
  }

  private asNullableNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private asNumber(value: unknown): number {
    const numericValue = this.asNullableNumber(value);
    return numericValue ?? 0;
  }

  private asBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }

    return Boolean(value);
  }

  private asSource(value: unknown): ForemanDetailedSummarySource {
    if (value === 'sparta' || value === 'viewpoint' || value === 'foremanApp') {
      return value;
    }

    return 'foremanApp';
  }

  private asRecordType(
    value: unknown
  ): ForemanDetailedSummary['records'][number]['recordType'] {
    if (
      value === 'labor' ||
      value === 'travel' ||
      value === 'perDiem' ||
      value === 'equipment'
    ) {
      return value;
    }

    return 'labor';
  }
}
